import React from 'react';
import { Dispatch } from 'redux';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Connector } from '@web3-react/types';
import { useWeb3ReactLegacy } from '../../utils/web3-compat';
import _every from 'lodash/every';
import _some from 'lodash/some';
import { DepositKeyInterface, StoreState } from '../../store/reducers';
import { Heading } from '../../pulsechain_components/Heading';
import { Paper } from '../../pulsechain_components/Paper';
import { Text } from '../../pulsechain_components/Text';
import { Button } from '../../pulsechain_components/Button';
import { Link } from '../../pulsechain_components/Link';
import { routesEnum } from '../../Routes';
import { KeyList } from './Keylist';
import { handleMultipleTransactions } from './transactionUtils';
import { TARGET_NETWORK_CHAIN_ID } from '../ConnectWallet/web3Utils';
import { WalletDisconnected } from '../ConnectWallet/WalletDisconnected';
import { WrongNetwork } from '../ConnectWallet/WrongNetwork';
import { WorkflowPageTemplate } from '../../pulsechain_components/WorkflowPage/WorkflowPageTemplate';
import {
  DepositStatus,
  DispatchTransactionStatusUpdateType,
  TransactionStatus,
  updateTransactionStatus,
  updateDepositFileKeys,
  updateDepositFileName,
} from '../../store/actions/depositFileActions';
import {
  DispatchWorkflowUpdateType,
  updateWorkflow,
  WorkflowStep,
} from '../../store/actions/workflowActions';
import { routeToCorrectWorkflowStep } from '../../utils/RouteToCorrectWorkflowStep';

// Prop definitions
interface OwnProps {}
interface StateProps {
  depositKeys: DepositKeyInterface[];
  workflow: WorkflowStep;
}
interface DispatchProps {
  dispatchTransactionStatusUpdate: DispatchTransactionStatusUpdateType;
  dispatchWorkflowUpdate: DispatchWorkflowUpdateType;
  dispatchClearDepositData: () => void;
}
type Props = StateProps & DispatchProps & OwnProps;

const _TransactionsPage = ({
  depositKeys,
  workflow,
  dispatchTransactionStatusUpdate,
  dispatchWorkflowUpdate,
  dispatchClearDepositData,
}: Props): JSX.Element => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { account, chainId, provider } = useWeb3ReactLegacy();

  const totalTxCount = depositKeys.filter(
    key => key.depositStatus !== DepositStatus.ALREADY_DEPOSITED
  ).length;

  const remainingTxCount = depositKeys.filter(
    file =>
      file.depositStatus !== DepositStatus.ALREADY_DEPOSITED &&
      (file.transactionStatus === TransactionStatus.READY ||
        file.transactionStatus === TransactionStatus.REJECTED)
  ).length;

  const allTxConfirmed = _every(
    depositKeys.map(
      file =>
        file.transactionStatus === TransactionStatus.SUCCEEDED ||
        file.depositStatus === DepositStatus.ALREADY_DEPOSITED
    )
  );

  const oneTxConfirmed = _some(
    depositKeys.map(
      file =>
        file.transactionStatus === TransactionStatus.SUCCEEDED ||
        file.depositStatus === DepositStatus.ALREADY_DEPOSITED
    )
  );

  const createButtonText = (): string => {
    if (totalTxCount === 1) {
      return formatMessage({ defaultMessage: 'Send deposit' });
    }
    if (totalTxCount === remainingTxCount && totalTxCount > 0) {
      return formatMessage(
        {
          defaultMessage: `Send all {totalTxCount} deposits`,
        },
        { totalTxCount }
      );
    }
    if (remainingTxCount > 1) {
      return formatMessage(
        {
          defaultMessage: `Send remaining {remainingTxCount} deposits`,
        },
        { remainingTxCount }
      );
    }
    if (remainingTxCount === 1) {
      return formatMessage({ defaultMessage: `Send last deposit` });
    }
    return formatMessage({ defaultMessage: 'No pending deposits' });
  };

  const createContinueButtonText = (): string => {
    if (!oneTxConfirmed) {
      return formatMessage({ defaultMessage: 'Continue' });
    }
    return allTxConfirmed
      ? formatMessage({ defaultMessage: 'Continue' })
      : formatMessage({
          defaultMessage: 'Continue without all transactions confirmed',
        });
  };

  const handleAllTransactionsClick = () => {
    handleMultipleTransactions(
      depositKeys.filter(
        key => key.depositStatus !== DepositStatus.ALREADY_DEPOSITED
      ),
      provider,
      account,
      dispatchTransactionStatusUpdate
    );
  };

  const handleSubmit = () => {
    // Allow submit if at this step or if user came back from a later step
    if (workflow >= WorkflowStep.TRANSACTION_SIGNING) {
      dispatchWorkflowUpdate(WorkflowStep.CONGRATULATIONS);
      // Navigate after state update
      setTimeout(() => {
        history.push(routesEnum.congratulationsPage);
      }, 100);
    }
  };

  const handleStartOver = () => {
    if (
      window.confirm(
        formatMessage({
          defaultMessage:
            'Are you sure you want to start over? This will clear all deposit data and transaction history.',
        })
      )
    ) {
      dispatchClearDepositData();
      dispatchWorkflowUpdate(WorkflowStep.OVERVIEW);
      history.push(routesEnum.landingPage);
    }
  };

  if (workflow < WorkflowStep.TRANSACTION_SIGNING) {
    return routeToCorrectWorkflowStep(workflow);
  }

  if (!account || !provider) return <WalletDisconnected />;

  if (chainId !== TARGET_NETWORK_CHAIN_ID) return <WrongNetwork />;

  return (
    <WorkflowPageTemplate
      title={formatMessage({ defaultMessage: 'Transactions' })}
    >
      <Paper className="mt20">
        <Heading level={3} size="small" color="blueMedium">
          {depositKeys.length === 1 ? (
            <FormattedMessage defaultMessage="Confirm deposit" />
          ) : (
            <FormattedMessage
              defaultMessage="Confirm deposits ({depositKeys})"
              values={{ depositKeys: depositKeys.length }}
            />
          )}
        </Heading>
        {depositKeys.length === 1 && (
          <Text className="mt20">
            <FormattedMessage defaultMessage="Submit a transaction to finish your deposit." />
          </Text>
        )}
        {depositKeys.length > 1 && (
          <>
            <Text className="mt20">
              <FormattedMessage defaultMessage="You must sign a transaction for every deposit you want to make." />
            </Text>
            <Text className="mt10">
              <FormattedMessage defaultMessage="You can start all the transactions at once, or start them individually." />
            </Text>
          </>
        )}
        {depositKeys.length >= 1 && <KeyList />}
        <div className="flex center mt30">
          <Button
            fullWidth
            rainbow
            label={createButtonText()}
            onClick={handleAllTransactionsClick}
            disabled={remainingTxCount === 0 || oneTxConfirmed}
          />
        </div>
      </Paper>

      <div className="flex center p30 mt20">
        <Link to={routesEnum.summaryPage}>
          <Button
            className="mr10"
            width={100}
            label={formatMessage({ defaultMessage: 'Back' })}
          />
        </Link>
        <Button
          width={300}
          rainbow
          label={createContinueButtonText()}
          disabled={!oneTxConfirmed}
          onClick={() => {
            handleSubmit();
          }}
        />
      </div>
      <div className="flex center mt10">
        <Text
          size="small"
          color="blueMedium"
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={handleStartOver}
        >
          <FormattedMessage defaultMessage="Start over with new deposit data" />
        </Text>
      </div>
    </WorkflowPageTemplate>
  );
};

const mapStateToProps = ({
  depositFile,
  workflow,
}: StoreState): StateProps => ({
  depositKeys: depositFile.keys,
  workflow,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  dispatchWorkflowUpdate: step => dispatch(updateWorkflow(step)),
  dispatchTransactionStatusUpdate: (pubkey, status, txHash) =>
    dispatch(updateTransactionStatus(pubkey, status, txHash)),
  dispatchClearDepositData: () => {
    dispatch(updateDepositFileKeys([]));
    dispatch(updateDepositFileName(''));
  },
});

export const TransactionsPage = connect<
  StateProps,
  DispatchProps,
  OwnProps,
  StoreState
>(
  mapStateToProps,
  mapDispatchToProps
)(_TransactionsPage);
